import { z } from 'zod';
import { CLOCK_ACTIONS, MISSED_CLOCK_OUTS, TIMESHEET_SOURCES } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf } from './common';

const clockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');

export const createTimesheetEntrySchema = z.object({
  employeeId: idSchema.optional().describe("Someone else's hours (timesheets.manage); your own when left out"),
  workDate: dateOnlySchema,
  startTime: clockSchema.nullable().optional().describe('With endTime; an end before the start runs past midnight'),
  endTime: clockSchema.nullable().optional(),
  minutes: z.number().int().min(1).max(1440).optional().describe('The time worked, when no start and end are given'),
  note: z.string().trim().max(500).nullable().optional(),
});
export type CreateTimesheetEntryInput = z.infer<typeof createTimesheetEntrySchema>;

export const updateTimesheetEntrySchema = patchOf(createTimesheetEntrySchema.omit({ employeeId: true }));
export type UpdateTimesheetEntryInput = z.infer<typeof updateTimesheetEntrySchema>;

export const timesheetQuerySchema = z.object({
  month: monthSchema,
  employeeId: idSchema.optional().describe("Someone else's month (timesheets.manage); your own when left out"),
});
export type TimesheetQuery = z.infer<typeof timesheetQuerySchema>;

export const approveTimesheetEntriesSchema = z.object({ entryIds: z.array(idSchema).min(1).max(400) });
export type ApproveTimesheetEntriesInput = z.infer<typeof approveTimesheetEntriesSchema>;

export const timesheetEntrySchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  workDate: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  minutes: z.number().int().nullable().describe('Null while a clock entry is open, and after a missed clock-out until a manager gives it an end'),
  note: z.string().nullable(),
  source: z.enum(TIMESHEET_SOURCES),
  clockedInAt: z.string().nullable().describe('What the clock recorded; a correction changes the start and end, never this'),
  clockedOutAt: z.string().nullable(),
  locationId: idSchema.nullable(),
  missedClockOut: z.enum(MISSED_CLOCK_OUTS).nullable().describe('SHIFT_END: closed at the planned end of the shift; FLAGGED: waits for a manager to give it an end'),
  approved: z.boolean(),
  approvedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type TimesheetEntryDto = z.infer<typeof timesheetEntrySchema>;

export const timesheetMonthSchema = z.object({
  employeeId: idSchema,
  employeeName: z.string(),
  month: z.string(),
  hourly: z.boolean().describe('Paid by the hour for some of the month, so payroll takes these hours'),
  locked: z.boolean().describe("The month's payroll is approved or paid; entries of the month no longer change"),
  approvedMinutes: z.number().int(),
  pendingMinutes: z.number().int(),
  entries: z.array(timesheetEntrySchema),
});
export type TimesheetMonthDto = z.infer<typeof timesheetMonthSchema>;

export const timesheetSummarySchema = z.object({
  month: z.string(),
  locked: z.boolean(),
  people: z.array(
    z.object({
      employeeId: idSchema,
      employeeName: z.string(),
      position: z.string().nullable(),
      hourly: z.boolean(),
      approvedMinutes: z.number().int(),
      pendingMinutes: z.number().int(),
    }),
  ),
});
export type TimesheetSummaryDto = z.infer<typeof timesheetSummarySchema>;

const plannedShiftSchema = z.object({
  date: z.string().describe('The day the shift starts on'),
  startTime: z.string(),
  endTime: z.string(),
  locationId: idSchema,
});

export const clockStatusSchema = z.object({
  employeeId: idSchema,
  open: z
    .object({ entryId: idSchema, clockedInAt: z.string(), locationId: idSchema.nullable() })
    .nullable()
    .describe('The clock entry running now'),
  onSite: z.boolean().describe("Whether this request may clock: the gym lists no allowed networks, or it comes from one of them"),
  shift: plannedShiftSchema.nullable().describe('The planned shift of today, or one from yesterday still running'),
});
export type ClockStatusDto = z.infer<typeof clockStatusSchema>;

export const clockResultSchema = z.object({ action: z.enum(CLOCK_ACTIONS), entry: timesheetEntrySchema });
export type ClockResultDto = z.infer<typeof clockResultSchema>;

export const clockBoardSchema = z.object({
  date: z.string(),
  time: z.string(),
  lateAfterMinutes: z.number().int(),
  people: z.array(
    z.object({
      employeeId: idSchema,
      name: z.string(),
      position: z.string().nullable(),
      locationId: idSchema.nullable().describe('Where they clocked in, else where their shift is'),
      shift: plannedShiftSchema.nullable(),
      clockedInAt: z.string().nullable().describe('The first clock-in for the shift, or of today without one'),
      clockedOutAt: z.string().nullable(),
      inNow: z.boolean(),
      lateMinutes: z.number().int().nullable().describe('How long after the planned start they clocked in, when later than the gym allows'),
      missing: z.boolean().describe('The planned shift has started and they have not clocked in'),
      extendedUntil: z.string().nullable().default(null).describe("A manager's extension of their working time still running, until then"),
    }),
  ),
  needsEnd: z.array(
    z.object({
      entryId: idSchema,
      employeeId: idSchema,
      name: z.string(),
      workDate: z.string(),
      startTime: z.string().nullable(),
      clockedInAt: z.string(),
      locationId: idSchema.nullable(),
    }),
  ),
});
export type ClockBoardDto = z.infer<typeof clockBoardSchema>;
